import React, { Dispatch, FC, PropsWithChildren, SetStateAction, createContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { DispatchProps, ModalContextProps } from '../../hooks/useModalDispatch';
import BadgeModal from '../../modals/BadgeModal';

export type ModalProps<T = void> = T & {
  handleClose: () => void;
};
export type ModalRenderProps = Record<string, unknown>;
export type ModalRenderer<P = ModalRenderProps> = FC<P> | undefined;
export type ModalState<P = ModalRenderProps> =
  | []
  | [
      modalComponent?: ModalRenderer<P>,
      modalProps?: DispatchProps<P>,
      contextProps?: ModalContextProps,
      pathname?: string | null,
    ];
export type ModalDispatch<P = ModalRenderProps> = Dispatch<SetStateAction<ModalState<P>>>;
export type ModalContext<P = ModalRenderProps> = [ModalDispatch<P>, ModalState<P>];

export const ModalContext = createContext<ModalContext>({} as ModalContext);

const ModalContextProvider = ({ badge, children }: PropsWithChildren<{ badge?: string }>) => {
  const location = useLocation();
  const [state, setModal] = useState<ModalState>(
    badge ? ([BadgeModal, { id: badge }, { size: 'sm' }, location.pathname] as unknown as ModalState) : [],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (location.pathname !== state[3]) {
      setModal([]);
    }
  }, [location.pathname]);

  return <ModalContext.Provider value={[setModal, state]}>{children}</ModalContext.Provider>;
};

export default ModalContextProvider;
